import React, { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";

import SimpleDialog from "../../../common/simpleDialog";
import { analyzeAssignmentCoverage, overrideStudentAdvisor } from "./apis";
import "./CoverageAnalysis.css";

// OWL-2294: Gap-and-Overlap Analysis. Extends the assignment simulation tooling (OWL-2293) with a
// department-wide coverage view: it evaluates every active student against every advisor rule and surfaces
//   - Gaps: students no advisor covers (manual "Assign to…" resolution), and
//   - Overlaps: students 2+ advisors cover, auto-resolved by the "most specific criterion wins" hierarchy
//     (Special Group > School > Zip Code > District). "Resolve" locks the winner in via a manual override
//     (OWL-2291), or a supervisor can pick another matched advisor to override the tie-break.
// The analysis is expensive (all students × all rules), so a school/district scope keeps it responsive.
// OWL-2294: `supervisedAdvisorIds` puts the modal in supervisor mode. When it's an array, the Assign/Resolve
// advisor choices are limited to advisors the supervisor directly supervises (the backend enforces the same
// scope on OverrideStudentAdvisor), so a supervisor can only resolve coverage by assigning to their own team.
// When it is null/undefined the modal runs in full manager mode with every advisor selectable.
const CoverageAnalysisModal = ({ open, onClose, schoolOptions, districtOptions, supervisedAdvisorIds = null }) => {
    const [districtId, setDistrictId] = useState("");
    const [schoolId, setSchoolId] = useState("");
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);

    // Local resolution state so resolved rows drop out of the tables without a full re-run.
    const [gaps, setGaps] = useState([]);
    const [overlaps, setOverlaps] = useState([]);
    const [gapAdvisorChoice, setGapAdvisorChoice] = useState({});
    const [overlapAdvisorChoice, setOverlapAdvisorChoice] = useState({});
    const [savingId, setSavingId] = useState(null);

    // OWL-2294: jump-nav — large gap lists make manual scrolling to the Overlaps section tedious, so the
    // Gaps/Overlaps summary stats double as anchors that smooth-scroll to their table.
    const gapsRef = useRef(null);
    const overlapsRef = useRef(null);

    const scrollToSection = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // Only offer schools within the chosen district (if any) for the school scope.
    const scopedSchools = useMemo(() => {
        const list = schoolOptions || [];
        if (!districtId) {
            return list;
        }
        return list.filter((s) => s.districtId === districtId);
    }, [schoolOptions, districtId]);

    const advisorOptions = (result && result.advisors) || [];

    // OWL-2294: in supervisor mode, restrict which advisors a coverage issue can be assigned to.
    const supervisorMode = Array.isArray(supervisedAdvisorIds);
    const canAssignTo = (advisorUserProfileId) =>
        !supervisorMode || supervisedAdvisorIds.includes(advisorUserProfileId);
    const assignableAdvisorOptions = supervisorMode
        ? advisorOptions.filter((a) => canAssignTo(a.userProfileId))
        : advisorOptions;

    const onDistrictChange = (value) => {
        setDistrictId(value);
        setSchoolId("");
    };

    const onRun = async () => {
        setRunning(true);
        try {
            const payload = {};
            if (schoolId) {
                payload.schoolId = schoolId;
            } else if (districtId) {
                payload.districtId = districtId;
            }
            const resp = await analyzeAssignmentCoverage(payload);
            const data = resp.data.result;
            setResult(data);
            setGaps(data.gaps || []);
            setOverlaps(data.overlaps || []);
            setGapAdvisorChoice({});
            // Default each overlap's resolution to the auto-resolved winner. In supervisor mode the winner may
            // not be one of the supervisor's advisors, so fall back to the first matching advisor they can
            // actually assign to (or blank, which disables Resolve for that row).
            const defaults = {};
            (data.overlaps || []).forEach((o) => {
                if (!supervisorMode) {
                    defaults[o.userProfileId] = o.resolvedAdvisorUserProfileId;
                    return;
                }
                const assignable = (o.advisors || []).filter((a) =>
                    supervisedAdvisorIds.includes(a.advisorUserProfileId)
                );
                const winner = assignable.find(
                    (a) => a.advisorUserProfileId === o.resolvedAdvisorUserProfileId
                );
                defaults[o.userProfileId] = (winner || assignable[0] || {}).advisorUserProfileId || "";
            });
            setOverlapAdvisorChoice(defaults);
        } catch (err) {
            toast.error("Could not run the coverage analysis. Please try again.");
        } finally {
            setRunning(false);
        }
    };

    const assignGap = async (gap) => {
        const advisorUserProfileId = gapAdvisorChoice[gap.userProfileId];
        if (!advisorUserProfileId) {
            toast.info("Choose a coach to assign this student to.");
            return;
        }
        setSavingId(gap.userProfileId);
        try {
            await overrideStudentAdvisor({
                userProfileId: gap.userProfileId,
                advisorUserProfileId,
                reason: "Coverage gap resolved via Coverage Analysis.",
            });
            setGaps((prev) => prev.filter((g) => g.userProfileId !== gap.userProfileId));
            toast.success("Student assigned.");
        } catch (err) {
            toast.error("Could not assign the student. Please try again.");
        } finally {
            setSavingId(null);
        }
    };

    const resolveOverlap = async (overlap) => {
        const advisorUserProfileId =
            overlapAdvisorChoice[overlap.userProfileId] ||
            (supervisorMode ? "" : overlap.resolvedAdvisorUserProfileId);
        if (!advisorUserProfileId) {
            toast.info("Choose a coach you supervise to resolve this overlap.");
            return;
        }
        setSavingId(overlap.userProfileId);
        try {
            await overrideStudentAdvisor({
                userProfileId: overlap.userProfileId,
                advisorUserProfileId,
                reason: "Coverage overlap resolved via Coverage Analysis.",
            });
            setOverlaps((prev) => prev.filter((o) => o.userProfileId !== overlap.userProfileId));
            toast.success("Overlap resolved.");
        } catch (err) {
            toast.error("Could not resolve the overlap. Please try again.");
        } finally {
            setSavingId(null);
        }
    };

    return (
        <SimpleDialog
            open={open}
            onClose={onClose}
            title="Coverage Analysis"
            loading={running}
            className="coverageDialog"
            maxWidth="lg"
        >
            <div className="coverage-body">
                <p className="coverage-help">
                    Find students no coach covers (<strong>gaps</strong>) and students more than one coach
                    covers (<strong>overlaps</strong>). Overlaps are auto-resolved by the priority hierarchy;
                    use <strong>Assign to…</strong> or <strong>Resolve</strong> to lock in an assignment.
                </p>

                <div className="coverage-scopeRow">
                    <label className="coverage-field">
                        <span>District</span>
                        <select value={districtId} onChange={(e) => onDistrictChange(e.target.value)} disabled={running}>
                            <option value="">All districts</option>
                            {(districtOptions || []).map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="coverage-field">
                        <span>School</span>
                        <select value={schoolId} onChange={(e) => setSchoolId(e.target.value)} disabled={running}>
                            <option value="">All schools{districtId ? " in district" : ""}</option>
                            {scopedSchools.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button type="button" className="coverage-run" onClick={onRun} disabled={running}>
                        {running ? "Analyzing..." : "Run Analysis"}
                    </button>
                </div>

                {result && (
                    <div className="coverage-results">
                        <div className="coverage-summary">
                            <div className="coverage-stat">
                                <div className="coverage-statValue">{result.totalStudentsEvaluated}</div>
                                <div className="coverage-statLabel">Students</div>
                            </div>
                            <button
                                type="button"
                                className="coverage-stat coverage-stat--link"
                                onClick={() => scrollToSection(gapsRef)}
                                title="Jump to Gaps"
                            >
                                <div className="coverage-statValue coverage-gapValue">{gaps.length}</div>
                                <div className="coverage-statLabel">Gaps</div>
                            </button>
                            <button
                                type="button"
                                className="coverage-stat coverage-stat--link"
                                onClick={() => scrollToSection(overlapsRef)}
                                title="Jump to Overlaps"
                            >
                                <div className="coverage-statValue coverage-overlapValue">{overlaps.length}</div>
                                <div className="coverage-statLabel">Overlaps</div>
                            </button>
                        </div>

                        <div className="coverage-hierarchy">
                            <span className="coverage-hierarchyLabel">Priority</span>
                            {(result.priorityHierarchy || []).map((c, i) => (
                                <React.Fragment key={c}>
                                    {i > 0 ? <span className="coverage-hierarchyArrow">&gt;</span> : null}
                                    <span className="coverage-hierarchyChip">{c}</span>
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="coverage-tableBlock" ref={gapsRef}>
                            <div className="coverage-tableHeader coverage-tableHeader--gap">Gaps ({gaps.length})</div>
                            {gaps.length === 0 ? (
                                <div className="coverage-empty">No gaps — every student in scope is covered.</div>
                            ) : (
                                <table className="coverage-table">
                                    <thead>
                                        <tr>
                                            <th>Student</th>
                                            <th>School</th>
                                            <th>Grade</th>
                                            <th>Reason Unmatched</th>
                                            <th>Assign to…</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gaps.map((g) => (
                                            <tr key={g.userProfileId}>
                                                <td>{g.name}</td>
                                                <td>{g.schoolName || "—"}</td>
                                                <td>{g.currentGrade || "—"}</td>
                                                <td className="coverage-reason">{g.reason}</td>
                                                <td className="coverage-actionCell">
                                                    <select
                                                        value={gapAdvisorChoice[g.userProfileId] || ""}
                                                        onChange={(e) =>
                                                            setGapAdvisorChoice((prev) => ({
                                                                ...prev,
                                                                [g.userProfileId]: e.target.value,
                                                            }))
                                                        }
                                                        disabled={savingId === g.userProfileId}
                                                    >
                                                        <option value="">Select coach…</option>
                                                        {assignableAdvisorOptions.map((a) => (
                                                            <option key={a.userProfileId} value={a.userProfileId}>
                                                                {a.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <button
                                                        type="button"
                                                        className="coverage-assignBtn"
                                                        onClick={() => assignGap(g)}
                                                        disabled={savingId === g.userProfileId}
                                                    >
                                                        Assign
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="coverage-tableBlock" ref={overlapsRef}>
                            <div className="coverage-tableHeader coverage-tableHeader--overlap">
                                Overlaps ({overlaps.length})
                            </div>
                            {overlaps.length === 0 ? (
                                <div className="coverage-empty">No overlaps — no student is covered by multiple coaches.</div>
                            ) : (
                                <table className="coverage-table">
                                    <thead>
                                        <tr>
                                            <th>Student</th>
                                            <th>School</th>
                                            <th>Grade</th>
                                            <th>Matching Coaches</th>
                                            <th>Overlap Reason</th>
                                            <th>Resolve</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {overlaps.map((o) => {
                                            // In supervisor mode only matching advisors the supervisor manages
                                            // can be chosen; if none match, this overlap belongs to another team.
                                            const overlapOptions = supervisorMode
                                                ? (o.advisors || []).filter((a) => canAssignTo(a.advisorUserProfileId))
                                                : o.advisors || [];
                                            const noAssignable = supervisorMode && overlapOptions.length === 0;
                                            return (
                                            <tr key={o.userProfileId}>
                                                <td>{o.name}</td>
                                                <td>{o.schoolName || "—"}</td>
                                                <td>{o.currentGrade || "—"}</td>
                                                <td>
                                                    <ul className="coverage-advisorList">
                                                        {(o.advisors || []).map((a) => (
                                                            <li
                                                                key={a.advisorUserProfileId}
                                                                className={a.isWinner ? "coverage-winner" : ""}
                                                            >
                                                                {a.advisorName}{" "}
                                                                <span className="coverage-criterion">({a.matchedCriterion})</span>
                                                                {a.isWinner ? <span className="coverage-winnerTag">winner</span> : null}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                                <td className="coverage-reason">{o.overlapReason}</td>
                                                <td className="coverage-actionCell">
                                                    {noAssignable ? (
                                                        <span className="coverage-notYourTeam">
                                                            None on your team
                                                        </span>
                                                    ) : (
                                                        <>
                                                            <select
                                                                value={
                                                                    overlapAdvisorChoice[o.userProfileId] ||
                                                                    (supervisorMode ? "" : o.resolvedAdvisorUserProfileId)
                                                                }
                                                                onChange={(e) =>
                                                                    setOverlapAdvisorChoice((prev) => ({
                                                                        ...prev,
                                                                        [o.userProfileId]: e.target.value,
                                                                    }))
                                                                }
                                                                disabled={savingId === o.userProfileId}
                                                            >
                                                                {overlapOptions.map((a) => (
                                                                    <option key={a.advisorUserProfileId} value={a.advisorUserProfileId}>
                                                                        {a.advisorName}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <button
                                                                type="button"
                                                                className="coverage-assignBtn"
                                                                onClick={() => resolveOverlap(o)}
                                                                disabled={savingId === o.userProfileId}
                                                            >
                                                                Resolve
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                <div className="coverage-footer">
                    <button type="button" className="coverage-close" onClick={onClose} disabled={running}>
                        Close
                    </button>
                </div>
            </div>
        </SimpleDialog>
    );
};

export default CoverageAnalysisModal;
